import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import ErrorMessage from '@components/Common/ErrorMessage';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import { getPromoCodesQuery } from '@constants/graphQLQueries';
import { formatDate } from '@helpers/date';
import { humanizeEnum } from '@helpers/labels';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined';
import { Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { type PromoCode, citiesLabel, expiryLabel, redemptionsLabel } from './promoCodes';
import './index.scss';

/**
 * Promo codes, as a CRM list. Super Admin only (NonHrRoute here, the BFF and the backend each
 * refuse everyone else): a code hands out free job posts.
 *
 * Filtering is instant and in memory, so it is the template's inline FilterRow; the chosen status
 * is in `?filter_status=` like every other list filter, so a filtered view can be shared.
 */

const COLUMNS = ['code', 'cities', 'status', 'redemptions', 'expiresAt', 'createdAt'];

/* The phone card: the code as the title, its status beside it. */
const CARD: ColumnsConfig = {
  code: { card: 'title', tablet: true },
  status: { card: 'status', tablet: true },
  cities: { tablet: true },
  redemptions: { tablet: true },
};

const STATUS_OPTIONS = [
  { label: 'All', value: '' },
  ...(['ACTIVE', 'INACTIVE', 'EXPIRED', 'FULL'] as const).map((s) => ({ label: humanizeEnum(s), value: s })),
];

const PAGE_SIZE = 50;

const PromoCodes: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const status = searchParams.get('filter_status') ?? '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchGraphQLApiData(getPromoCodesQuery());
      setCodes(res?.data?.promoCodes ?? []);
    } catch (e: any) {
      console.error('[PromoCodes] load failed', e?.message);
      setError('Could not load promo codes. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () => (status ? codes.filter((c) => c.status === status) : codes),
    [codes, status],
  );

  const setStatus = (value: string) => {
    setPage(0);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('filter_status', value);
      else next.delete('filter_status');
      return next;
    });
  };

  const body = () => {
    if (loading && codes.length === 0) return <SkeletonLoader rows={8} columns={COLUMNS.length} />;
    if (error) return <ErrorMessage message={error} onRetry={load} />;
    if (codes.length === 0) {
      return (
        <EmptyState
          icon={<DiscountOutlinedIcon />}
          title="No promo codes yet"
          body="A promo code gives an employer one free job post for jobs in the cities you choose. Create one to start the city-expansion offer."
        />
      );
    }
    if (visible.length === 0) {
      return <EmptyState compact title="No codes match this filter" />;
    }
    return (
      <MintlyTable
        pagination={{
          enabled: true,
          page,
          rowsPerPage: PAGE_SIZE,
          totalNumberOfRows: visible.length,
          handleChangePage: (_e: unknown, next: number) => setPage(next),
        }}
        rows={visible.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((c) => ({ ...c, id: c._id }))}
        selectors={COLUMNS}
        columns={CARD}
        onRowClick={(row: PromoCode) => navigate(`/promo-codes/${row._id}`)}
        tableHeadRow={{
          code: <>Code</>,
          cities: <>Cities</>,
          status: <>Status</>,
          redemptions: <>Redemptions</>,
          expiresAt: <>Expires</>,
          createdAt: <>Created</>,
        }}
        tableBodyRow={{
          code: (row: PromoCode) => <Typography component="span" className="promo-code">{row.code}</Typography>,
          cities: (row: PromoCode) => <>{citiesLabel(row.targetCities)}</>,
          status: (row: PromoCode) => <CRMStatusChip status={row.status} />,
          redemptions: (row: PromoCode) => <span className="promo-figure">{redemptionsLabel(row)}</span>,
          expiresAt: (row: PromoCode) => <>{expiryLabel(row.expiresAt)}</>,
          createdAt: (row: PromoCode) => <>{formatDate(row.createdAt)}</>,
        }}
      />
    );
  };

  return (
    <CRMPageTemplate
      icon={<DiscountOutlinedIcon />}
      title="Promo codes"
      subtitle="Free job posts for employers in selected cities."
      totalCount={visible.length}
      onAddNew={() => navigate('/promo-codes/new')}
      addNewLabel="New promo code"
      inlineFilters={(
        <SelectFormField
          required={false}
          name="filter_status"
          label="Status"
          size="small"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(_n, v) => setStatus(String(v ?? ''))}
        />
      )}
    >
      {body()}
    </CRMPageTemplate>
  );
};

export default PromoCodes;
